import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import RecordShell from "../../components/RecordShell";
import { Badge, EtapeIcon, INTENT, fmtDate, PAYMENT_STATUS, flattenEtapes, phaseByEtape, eur, validatedByLabel } from "../../lib/helpers";
import Card from "../../components/Card";
import PhaseBoard from "../../components/PhaseBoard";
import RdvCard from "../../components/RdvCard";
import FormCard from "../../components/FormCard";
import { Modal } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthImg from "../../components/AuthImg";
import AuthFileLink from "../../components/AuthFileLink";
import { useConfirm } from "../../components/ConfirmDialog";
import Conversation, { agencyAuthor } from "../../components/Conversation";
import { ASSISTANT_ID } from "../../components/AssistantAvatar";
import { EmptyState } from "../../components/Feedback";

export default function ClientProjectDetail() {
  const { confirm } = useConfirm();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null);
  const [modText, setModText] = useState("");
  const [formModal, setFormModal] = useState(null);
  const [formDirty, setFormDirty] = useState(false);
  const srcRef = useRef();

  // Projet supprimé, d'une autre entreprise ou adresse erronée (lien d'un vieil e-mail) : un message et un chemin de
  // retour, plus une page vide (recette du 15/10, ANO-04).
  const load = useCallback(() => api.get(`/client/projects/${id}`).then((r) => { setP(r.data); setLoadError(null); })
    .catch((e) => setLoadError([403, 404].includes(e?.response?.status) ? "missing" : "failed")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!p && loadError) return (
    <EmptyState icon="FolderX" title={loadError === "missing" ? "Projet introuvable" : "Impossible d'afficher ce projet"}
      hint={loadError === "missing" ? "Il a peut-être été retiré, ou il n'est pas rattaché à votre entreprise." : "Vérifiez votre connexion, puis rechargez la page."}
      action={<button onClick={() => nav("/projets")} className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Retour à mes projets</button>} />
  );
  if (!p) return null;

  // Le grisé suit l'interactivité (Journal Horizon) : une étape est grisée si et
  // seulement si le client ne peut pas interagir avec elle — non démarrée, ou
  // appartenant à une phase qui n'est pas en_cours (a_venir, ou cloturee quel que
  // soit le statut de l'étape — la trace délibérée du Lot 3b documente l'arbitrage
  // de l'agence, elle ne rouvre pas la porte au client). isLocked (plus bas) calcule ça
  // directement depuis la phase (Lot 6) ; ne sert plus qu'à awaitingCount (Lot 4d,
  // pas d'appel à l'action sur une étape grisée).
  const phaseMap = phaseByEtape(p);

  const uploadSource = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); try { await api.post(`/client/projects/${id}/sources`, fd); toast.success("Document envoyé"); } catch { toast.error("Échec de l'envoi"); } if (srcRef.current) srcRef.current.value = ""; load(); };
  const deleteSource = async (did, name) => { if (!(await confirm({ title: `Supprimer « ${name} » ?`, confirmLabel: "Supprimer", tone: "danger" }))) return; try { await api.delete(`/client/projects/${id}/sources/${did}`); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); } };
  // Erreur affichée (30/09) : refus du serveur, ou « modifié ailleurs » — la fiche est rechargée dans tous les cas.
  const validate = async (key) => {
    try { await api.post(`/client/projects/${id}/etapes/${key}/validate`); toast.success("Étape validée"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Validation impossible"); }
    load();
  };
  const submitForm = async (key, payload) => {
    await api.post(`/client/projects/${id}/etapes/${key}/form/submit`, payload);
    load();
  };
  const submitMod = async () => {
    try {
      await api.post(`/client/projects/${id}/etapes/modify`, { etape_key: modal, message: modText });
      toast.success("Demande envoyée : elle s'ajoute à la conversation du projet"); setModal(null); setModText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    load();
  };
  // Question posée AVANT l'animation de fermeture (beforeClose du Modal) : refuser laisse le formulaire ouvert et visible.
  const confirmCloseForm = async () => !formDirty || confirm({ title: "Fermer le formulaire ?", message: "Vos réponses ne seront pas conservées.", confirmLabel: "Fermer quand même", cancelLabel: "Continuer le formulaire" });
  const closeFormModal = () => {
    setFormModal(null);
    setFormDirty(false);
  };
  // Conversation du projet (01/10) : un message peut porter la pastille d'une étape.
  const sendMessage = async (text, etapeKey) => { await api.post(`/client/projects/${id}/comments`, { text, etape_key: etapeKey }); await load(); };

  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-0.5">{p.type_label}</div>
          <h1 className="font-display text-2xl text-marine">{p.name}</h1>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-marine/10">
        <div className="flex-1 h-2.5 bg-sable border border-marine/30 rounded-full overflow-hidden"><div className="h-full bg-marine" style={{ width: `${p.progress}%` }} /></div>
        <span className="font-display text-xl text-marine">{p.progress}%</span>
      </div>
    </div>
  );

  const renderApercu = () => (
    <div className="space-y-6">
      {(p.team || []).length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4" data-testid="client-project-team"><h2 className="font-display text-xl text-marine">Votre équipe</h2></div>
          <div className="flex flex-wrap gap-4">
            {(p.team || []).map((t, i) => (
              <div key={t.id || i} data-testid={`client-team-${i}`} className="flex items-center gap-3">
                <span className={`w-11 h-11 rounded-full flex items-center justify-center overflow-hidden text-sm font-display text-white shrink-0 ${t.agency ? "bg-bordeaux" : "bg-marine"}`}>
                  {t.avatar_url ? <AuthImg path={t.avatar_url} alt={t.name} className="w-full h-full object-cover" /> : t.name?.[0]?.toUpperCase()}
                </span>
                <div>
                  <div className="text-sm font-medium text-marine">{t.name}</div>
                  {t.job_role && <div className={`text-xs ${t.agency ? "text-bordeaux" : "text-marine/80"}`}>{t.job_role}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {(p.links || []).length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="client-project-links">
          {(p.links || []).map((l, i) => (
            <a key={l.id || i} href={l.url} target="_blank" rel="noreferrer" data-testid={`client-link-${i}`}
              className="flex items-center justify-between gap-3 bg-bordeaux text-white rounded-sm px-5 py-4 hover:bg-bordeaux-hover transition-colors">
              <div className="flex items-center gap-3"><Icons.Link2 size={16} className="shrink-0" /><div><div className="text-sm font-medium">{l.name}</div>{l.description && <div className="text-xs text-white/80">{l.description}</div>}</div></div>
              <Icons.ExternalLink size={16} className="shrink-0" />
            </a>
          ))}
        </div>
      )}
      {(p.team || []).length === 0 && (p.links || []).length === 0 && <p className="text-sm text-marine/80">Suivez ci-dessus l'avancement global. Consultez les onglets pour les étapes, fichiers et échanges.</p>}
    </div>
  );

  // Une étape attend l'entreprise si elle est « en attente de validation » dans une phase en cours : même règle que le
  // badge de l'onglet (awaitingCount) et que le point ambre de la rangée des phases sur téléphone.
  const isLocked = (et, ph) => et.status === "non_demarre" || ph.status !== "en_cours";
  const isGateOf = (et, ph) => !isLocked(et, ph) && et.status === "en_attente_client";

  const renderEtapeCard = (et, ph, enColonnes) => {
    const locked = isLocked(et, ph);
    const isGate = isGateOf(et, ph);
    return (
      <div key={et.key} data-testid={`etape-${et.key}`} className={`bg-sable rounded-sm border p-5 transition-opacity ${isGate ? "border-ambre/40 ring-1 ring-ambre/20" : "border-marine/10"} ${locked ? "carte-a-venir" : ""}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {/* shrink-0 : un libellé long tassait l'icône de 2 px et décalait la pastille placée dessous. */}
            <span className="flex shrink-0">{locked ? <Icons.Lock size={16} className="text-muet" /> : <EtapeIcon status={et.status} size={16} />}</span>
            <div>
              <div className="text-marine font-medium">{et.name} {et.blocking && <span className="text-[9px] uppercase tracking-wider whitespace-nowrap bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm ml-1" title="Les étapes suivantes de cette phase ne démarrent pas tant que celle-ci n'est pas validée">Étape obligatoire</span>}</div>
              {et.validated_at && <div className="text-xs text-marine/80">Validée le {fmtDate(et.validated_at)} {validatedByLabel(et, "client")}</div>}
              {et.planned_date && et.status !== "valide" && <div className="text-xs text-marine/80">Prévu le {fmtDate(et.planned_date)}</div>}
            </div>
          </div>
          {/* Décision F1 (18/08) : sous le libellé sur téléphone et tablette (pl-7 = icône 16 px + écart 12 px), à droite sur
              ordinateur ; dans une colonne de phase, toujours sous le libellé (01/10 : des cartes alignées). */}
          <div className={enColonnes ? "flex basis-full pl-7" : "flex basis-full pl-7 lg:basis-auto lg:pl-0"}>
            {isGate
              ? <span data-testid={`etape-awaiting-${et.key}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[9px] font-medium ${INTENT.attente.cls}`}>En attente de votre validation</span>
              : <Badge status={et.status} />}
          </div>
        </div>
        {et.description && <div className="text-xs text-marine/80 mt-2 whitespace-pre-line" data-testid={`etape-desc-view-${et.key}`}>{et.description}</div>}
        {!locked && <RdvCard rdv={et} testId={et.key} />}
        {!locked && et.form && et.form.status !== "completed" && (
          <button type="button" onClick={() => setFormModal(et.key)} data-testid={`open-form-${et.key}`}
            className="border border-marine text-marine rounded-sm px-2.5 py-1.5 text-xs hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5 mt-3">
            <Icons.ClipboardList size={14} /> Compléter le formulaire
          </button>
        )}
        {!locked && et.form && et.form.status === "completed" && (
          <div className="text-xs text-marine/80 mt-3 flex items-center gap-1.5" data-testid={`form-completed-${et.key}`}>
            <Icons.Lock size={16} className="text-marine" /> Formulaire complété
          </div>
        )}
        {/* Lot 6 : les messages « phase non commencée / terminée » sont retirés —
            déjà portés par l'en-tête de la Card (nom + statut). Seul le message de
            position intra-phase (étape pas encore atteinte dans une phase active)
            apporte une information que l'en-tête ne donne pas. */}
        {locked && ph.status === "en_cours" && (
          <div className="text-xs text-marine/80 mt-3 flex items-center gap-1.5" data-testid={`etape-locked-${et.key}`}><Icons.Lock size={16} className="text-marine" /> En attente de l'avancement des étapes précédentes.</div>
        )}
        {isGate && (
          <div className="flex flex-wrap gap-3 mt-4">
            {/* 12 px, marges de l'admin (« + Ajouter un RDV ») : décision du 28/09, même taille de bouton dans la carte d'étape pour les trois rôles.
              flex-wrap : quand ils ne tiennent pas côte à côte (colonne, téléphone), les deux boutons passent l'un sous l'autre
              au lieu de couper leur libellé (décisions du 01/10, colonnes puis phase seule). */}
            <button onClick={() => validate(et.key)} data-testid={`validate-${et.key}`} className="bg-marine text-white rounded-sm px-2.5 py-1.5 text-xs hover:bg-marine-hover active:scale-95 transition-all flex items-center gap-1.5"><Icons.Check size={14} /> Valider cette étape</button>
            <button onClick={() => setModal(et.key)} data-testid={`modify-${et.key}`} className="border border-bordeaux text-bordeaux rounded-sm px-2.5 py-1.5 text-xs hover:bg-bordeaux hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={14} /> Demander une modification</button>
          </div>
        )}
      </div>
    );
  };

  // Phases en colonnes (décisions du 13/08 et du 01/10) : voir components/PhaseBoard.js.
  const renderEtapes = () => (
    <PhaseBoard phases={p.phases || []} testid="client-etapes" renderEtape={renderEtapeCard}
      awaitingKey={(ph) => (ph.etapes || []).find((et) => isGateOf(et, ph))?.key || null} />
  );

  const renderFichiers = () => (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-xl text-marine mb-4">Fichiers partagés par METARA</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {p.deliverables.length === 0 && <p className="text-sm text-marine/80">Aucun fichier pour le moment.</p>}
          {p.deliverables.map((d) => {
            const expired = d.expired || (d.days_left != null && d.days_left < 0);
            return (
              <div key={d.id} className="bg-sable rounded-sm border border-marine/10 p-4">
                <div className="flex items-center gap-2 mb-2"><Icons.FileText size={16} className="text-marine" /><span className="text-sm font-medium truncate">{d.name}</span></div>
                {expired ? <div className="text-xs text-rouge">Fichier expiré · Faire une demande pour le récupérer</div> : (
                  <>
                    <div className="text-xs text-marine/80 mb-2">Expire dans {d.days_left} jour(s)</div>
                    <AuthFileLink path={d.path} data-testid={`download-${d.id}`} className="inline-flex items-center gap-1 text-sm text-marine hover:underline"><Icons.Download size={16} /> Télécharger</AuthFileLink>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl text-marine">Mes documents sources</h2>
          <button onClick={() => srcRef.current?.click()} data-testid="client-upload-source" className="border border-marine text-marine rounded-sm px-3 py-1.5 text-sm hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Upload size={16} /> Envoyer un document</button>
          <input ref={srcRef} type="file" className="hidden" onChange={uploadSource} />
        </div>
        <p className="text-xs text-marine/80 mb-3">Vidéos, exports, fichiers bruts à transmettre à METARA pour ce projet.</p>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="client-sources">
          {(p.client_sources || []).length === 0 && <p className="text-sm text-marine/80">Aucun document envoyé.</p>}
          {(p.client_sources || []).map((d) => (
            <div key={d.id} className="bg-sable rounded-sm border border-bleu/20 p-4 flex items-center justify-between">
              <div className="min-w-0"><div className="flex items-center gap-2"><Icons.FileVideo size={16} className="text-bleu shrink-0" /><span className="text-sm font-medium truncate">{d.name}</span></div><div className="text-xs text-marine/80 mt-0.5">{fmtDate(d.uploaded_at)}</div></div>
              <div className="flex items-center gap-1 shrink-0">
                <AuthFileLink path={d.path} title="Télécharger" className="p-1.5 text-marine hover:bg-marine-50 rounded-sm inline-flex"><Icons.Download size={16} /></AuthFileLink>
                <button onClick={() => deleteSource(d.id, d.name)} data-testid={`delete-source-${d.id}`} title="Supprimer" className="p-1.5 text-rouge hover:bg-rouge-50 rounded-sm"><Icons.Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {p.mission_files && p.mission_files.length > 0 && (
        <div>
          <h2 className="font-display text-xl text-marine mb-4">Documents de production</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="client-mission-files">
            {p.mission_files.map((d, i) => (
              <div key={i} className="bg-sable rounded-sm border border-marine/10 p-4">
                <div className="flex items-center gap-2 mb-1"><Icons.Clapperboard size={16} className="text-bordeaux" /><span className="text-sm font-medium truncate">{d.name}</span></div>
                {d.description && <div className="text-xs text-marine/80 mb-2">{d.description}</div>}
                {d.mission_type && <div className="text-xs text-marine/80 mb-2">{d.mission_type}</div>}
                {d.path && <AuthFileLink path={d.path} className="inline-flex items-center gap-1 text-sm text-marine hover:underline"><Icons.Download size={16} /> Télécharger</AuthFileLink>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Échéances et factures (recette du 15/10, ANO-16 ; décisions de Mélilia, questions 5 et 19 à 22, sur ébauche) : toutes
  // les échéances du projet, chacune avec sa facture dès qu'elle est montrée (même règle qu'avant), la ligne « Projet »,
  // et le détail des versements d'une échéance réglée en plusieurs fois. Jamais : note interne, relances, note d'un
  // paiement. Jusque-là, seules les échéances déjà facturées apparaissaient (« Mes factures »).
  const renderFacturation = () => {
    const rows = (p.installments || []).filter((it) => it.status !== "annule")
      .sort((a, b) => String(a.due_date || "9999").localeCompare(String(b.due_date || "9999")));
    const cur = rows[0]?.currency;
    const sum = (k) => rows.reduce((t, it) => t + (Number(it[k]) || 0), 0);
    const cols = "grid-cols-[minmax(0,1.6fr)_96px_100px_100px_100px_150px]";
    const invoiceOf = (it, cls) => {
      const hasFile = !!it.invoice_url, href = !hasFile ? (it.invoice_link || null) : null;
      if (!hasFile && !href) return <span className="text-xs text-marine/80">Facture à venir</span>;
      const LinkTag = hasFile ? AuthFileLink : "a";
      const linkProps = hasFile ? { path: it.invoice_url } : { href, target: "_blank", rel: "noreferrer" };
      return <LinkTag {...linkProps} data-testid={`client-invoice-link-${it.id}`} className={cls}><Icons.FileText size={14} className="shrink-0" /> Facture {it.invoice_number || ""}</LinkTag>;
    };
    const lateButton = (it) => {
      const hasFile = !!it.invoice_url, href = !hasFile ? (it.invoice_link || null) : null;
      if (it.status !== "en_retard" || (!hasFile && !href)) return null;
      const LinkTag = hasFile ? AuthFileLink : "a";
      const linkProps = hasFile ? { path: it.invoice_url } : { href, target: "_blank", rel: "noreferrer" };
      return (
        <LinkTag {...linkProps} data-testid={`client-invoice-download-${it.id}`}
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-sm px-4 py-2.5 text-sm font-medium text-white bg-rouge hover:bg-rouge-hover transition-colors">
          <Icons.Download size={16} /> Télécharger la facture{it.invoice_number ? ` ${it.invoice_number}` : ""} (paiement en retard)
        </LinkTag>
      );
    };
    const payments = (it) => (it.status === "partiellement_paye" || (it.payments || []).length > 1) && (it.payments || []).length > 0 && (
      <ul className="text-xs text-marine/80 space-y-0.5" data-testid={`client-installment-payments-${it.id}`}>
        {it.payments.map((pay, k) => <li key={k}>Versement du {fmtDate(pay.date)} : {eur(pay.amount, it.currency)}</li>)}
      </ul>
    );
    return (
      <div>
        <h2 className="font-display text-xl text-marine mb-2">Échéances et factures</h2>
        <p className="text-xs text-marine/80 mb-4">Chaque échéance du projet, avec sa facture dès qu'elle est disponible.</p>
        {rows.length === 0 ? <p className="text-sm text-marine/80">Aucune échéance pour ce projet.</p> : (<>
          <div className="hidden md:block bg-sable rounded-sm border border-marine/10 overflow-hidden" data-testid="client-installments">
            <div className={`grid ${cols} gap-3 px-5 py-2.5 border-b border-marine/10 text-xs uppercase tracking-wider text-marine/80`}>
              <span>Échéance</span><span className="text-right">Date</span><span className="text-right">Montant</span><span className="text-right">Réglé</span><span className="text-right">Reste</span><span className="text-right">État</span>
            </div>
            <div className="divide-y divide-marine/5">
              {rows.map((it) => {
                const s = PAYMENT_STATUS[it.status] || PAYMENT_STATUS.a_venir;
                return (
                  <div key={it.id} data-testid={`client-installment-${it.id}`} className="px-5 py-3.5 bg-creme">
                    <div className={`grid ${cols} gap-3 items-center text-sm text-marine tabular-nums`}>
                      <div className="min-w-0"><div className="font-medium truncate">{it.label}</div>{invoiceOf(it, "text-xs text-marine hover:underline inline-flex items-center gap-1")}</div>
                      <div className="text-right text-xs text-marine/80">{it.due_date ? fmtDate(it.due_date) : "—"}</div>
                      <div className="text-right">{eur(it.amount, it.currency)}</div>
                      <div className="text-right">{Number(it.paid_amount) > 0 ? eur(it.paid_amount, it.currency) : <span className="text-marine/80">—</span>}</div>
                      <div className="text-right">{Number(it.outstanding) > 0 ? eur(it.outstanding, it.currency) : <span className="text-marine/80">—</span>}</div>
                      <div className="text-right"><span className={`text-[10px] px-2 py-0.5 rounded-sm border whitespace-nowrap ${s.cls}`}>{s.label}</span></div>
                    </div>
                    {payments(it) && <div className="mt-1.5">{payments(it)}</div>}
                    {lateButton(it)}
                  </div>
                );
              })}
            </div>
            <div className={`grid ${cols} gap-3 px-5 py-3 bg-sable-clair border-t border-marine/10 text-sm font-semibold text-marine tabular-nums`} data-testid="client-installments-total">
              <span>Projet</span><span /><span className="text-right">{eur(sum("amount"), cur)}</span><span className="text-right">{eur(sum("paid_amount"), cur)}</span><span className="text-right">{eur(sum("outstanding"), cur)}</span><span />
            </div>
          </div>
          {/* Téléphone et tablette : une carte par échéance */}
          <div className="md:hidden space-y-2" data-testid="client-installments-cards">
            {rows.map((it) => {
              const s = PAYMENT_STATUS[it.status] || PAYMENT_STATUS.a_venir;
              return (
                <div key={it.id} className="bg-creme rounded-sm border border-marine/10 p-3.5 grid grid-cols-1 gap-1.5 text-sm text-marine">
                  <div className="flex items-start justify-between gap-2"><span className="font-medium min-w-0 break-words">{it.label}</span><span className={`text-[10px] px-2 py-0.5 rounded-sm border whitespace-nowrap shrink-0 ${s.cls}`}>{s.label}</span></div>
                  <div className="flex justify-between text-xs text-marine/80 tabular-nums"><span>{it.due_date ? fmtDate(it.due_date) : "—"}</span><span>{eur(it.amount, it.currency)}</span></div>
                  {(Number(it.paid_amount) > 0 && Number(it.outstanding) > 0) && <div className="flex justify-between text-xs text-marine/80 tabular-nums"><span>Réglé {eur(it.paid_amount, it.currency)}</span><span>Reste {eur(it.outstanding, it.currency)}</span></div>}
                  {payments(it)}
                  <div>{invoiceOf(it, "text-xs text-marine hover:underline inline-flex items-center gap-1")}</div>
                  {lateButton(it)}
                </div>
              );
            })}
            <div className="bg-sable-clair rounded-sm border border-marine/10 p-3.5 grid grid-cols-1 gap-1 text-sm font-semibold text-marine tabular-nums">
              <div className="flex justify-between"><span>Projet</span><span>{eur(sum("amount"), cur)}</span></div>
              <div className="flex justify-between text-xs font-normal text-marine/80"><span>Réglé {eur(sum("paid_amount"), cur)}</span><span>Reste {eur(sum("outstanding"), cur)}</span></div>
            </div>
          </div>
        </>)}
      </div>
    );
  };

  const renderConversation = () => {
    const etapes = flattenEtapes(p);
    const names = Object.fromEntries(etapes.map((e) => [e.key, e.name]));
    const messages = (p.comments || []).map((c) => ({
      id: c.id, mine: c.author_role === "client", text: c.text, created_at: c.created_at,
      author: c.author_role === "admin" ? agencyAuthor(c.author_name) : c.author_name,
      assistant: c.author_id === ASSISTANT_ID,
      badge: c.etape_key ? { kind: "Étape", label: names[c.etape_key] || "" } : null,
      kindLabel: c.kind === "modification" ? "Demande de modification" : "",
    }));
    return (
      <Conversation messages={messages} selectLabel="Étape concernée" options={etapes.map((e) => ({ value: e.key, label: e.name }))}
        placeholder="Écrire à Metara…" onSend={sendMessage} highlightId={sp.get("message")} testid="conversation" />
    );
  };

  // Lot 4d : même règle que le grisé (phaseMap défini plus haut) — le badge de
  // l'onglet ne compte que les étapes réellement actionnables.
  const allEtapes = flattenEtapes(p);
  const awaitingCount = allEtapes.filter((x) => x.status === "en_attente_client" && phaseMap[x.key]?.status === "en_cours").length;
  const openForm = formModal ? allEtapes.find((x) => x.key === formModal)?.form : null;
  const sections = [
    { id: "apercu", label: "Vue d'ensemble", icon: "LayoutDashboard", render: renderApercu },
    { id: "etapes", label: "Étapes", icon: "ListChecks", render: renderEtapes, badge: awaitingCount || undefined },
    { id: "fichiers", label: "Fichiers", icon: "FolderOpen", render: renderFichiers },
    { id: "facturation", label: "Facturation", icon: "Wallet", render: renderFacturation },
    { id: "conversation", label: "Conversation", icon: "MessagesSquare", render: renderConversation },
  ];

  return (
    <div>
      <button onClick={() => nav("/projets")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Retour aux projets</button>
      <RecordShell
        testid="client-project-record"
        breadcrumb={[{ label: "Mes Projets", to: "/projets" }, { label: p.type_label }, { label: p.name }]}
        header={header}
        sections={sections}
      />

      {/* Fenêtre partagée (recette du 15/10, ANO-12) : annoncée comme fenêtre aux lecteurs d'écran, clavier gardé à
          l'intérieur, Échap pour fermer — elle était dessinée à la main, sans rien de cela. */}
      {modal && (
        <Modal title="Demander une modification" onClose={() => setModal(null)}>
          <p className="text-sm text-marine/80 mb-3" data-testid="mod-hint">Étape « {(flattenEtapes(p).find((e) => e.key === modal) || {}).name} » · votre demande s'ajoute à la conversation du projet ; Metara vous y répond.</p>
          <textarea value={modText} onChange={(e) => setModText(e.target.value)} data-testid="mod-text" data-autofocus rows={4} placeholder="Décrivez la modification souhaitée..." className="w-full border border-marine/20 rounded-sm p-3 text-sm outline-none focus:ring-1 focus:ring-marine mb-4" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setModal(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
            <button onClick={submitMod} disabled={!modText.trim()} data-testid="mod-submit" className="bg-rouge text-white rounded-sm px-4 py-2 text-sm hover:bg-rouge-hover disabled:opacity-50">Envoyer</button>
          </div>
        </Modal>
      )}

      {formModal && (
        <Modal onClose={closeFormModal} beforeClose={confirmCloseForm} wide
          title={<><span className="font-semibold text-marine">Formulaire</span>{openForm?.name && <span className="font-normal text-marine/80"> — {openForm.name}</span>}</>}>
          <FormCard key={formModal} form={openForm}
            onSubmit={(payload) => submitForm(formModal, payload)} testId={formModal} onDirtyChange={setFormDirty} />
        </Modal>
      )}
    </div>
  );
}
